import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Complaint } from '../../core/models/complaint.model';
import { ComplaintService } from '../../core/services/complaint.service';
import { COLUMNS, buildSearchText, fmtDate, statusTone } from './complaint-fields';
import { Segment, splitHighlight } from './complaint-highlight';

/** Below this, every case matches "" and the page would just dump the whole table. */
const MIN_QUERY_LENGTH = 2;
/** Characters of context kept on each side of a match inside a long field (e.g. OCR text). */
const SNIPPET_RADIUS = 70;

export type { Segment };
export { splitHighlight };

export interface FieldSnippet {
  label: string;
  segments: Segment[];
}

/** Crops a long field down to a window around its first match, so a full OCR transcript
 *  doesn't dump onto the results page — same idea as a Google result snippet. */
function windowAround(text: string, query: string, radius = SNIPPET_RADIUS): string {
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text.length > radius * 2 ? `${text.slice(0, radius * 2)}…` : text;
  const start = Math.max(0, idx - radius);
  const end = Math.min(text.length, idx + query.length + radius);
  return (start > 0 ? '…' : '') + text.slice(start, end).trim() + (end < text.length ? '…' : '');
}

@Component({
  selector: 'app-complaint-search',
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: './complaint-search.component.html',
  styleUrl: './complaint-search.component.scss',
})
export class ComplaintSearchComponent {
  private readonly store = inject(ComplaintService);

  readonly query = signal('');
  readonly fmtDate = fmtDate;
  readonly statusTone = statusTone;

  private readonly normalizedQuery = computed(() => this.query().trim().toLowerCase());

  readonly ready = computed(() => this.normalizedQuery().length >= MIN_QUERY_LENGTH);

  /** Searches every case the officer is authorized to see — no tab or "assigned to me" scoping. */
  readonly results = computed(() => {
    const q = this.normalizedQuery();
    if (q.length < MIN_QUERY_LENGTH) return [];
    return this.store.list().filter((item) => buildSearchText(item).includes(q));
  });

  /** Highlighted title — the search term bolded wherever it appears in the case subject. */
  titleSegments(item: Complaint): Segment[] {
    const title = item.subject || item.category || '—';
    return splitHighlight(title, this.normalizedQuery());
  }

  /** The single best matching field, as a highlighted snippet — like a Google result excerpt.
   *  Picks the first field (in COLUMNS' relevance order) that actually contains the query,
   *  skipping the subject — that match is already visible highlighted in the title above. */
  snippetFor(item: Complaint): FieldSnippet | null {
    const q = this.normalizedQuery();
    if (!q) return null;
    for (const c of COLUMNS) {
      if (c.key === 'subjectEnglish') continue;
      const text = c.get(item);
      if (text && text !== '—' && text.toLowerCase().includes(q)) {
        return { label: c.label, segments: splitHighlight(windowAround(text, q), q) };
      }
    }
    return null;
  }
}
